<app-page-layout
  [pageTitle]="'Application'"
  [subtitle]="'Manage applications'">

  <div page-actions class="flex items-center gap-2">
    <p-toast/>
    <p-button
      label="Add Application"
      icon="pi pi-plus"
      (onClick)="openAddApplicationDialog()"
      severity="primary"
      size="small"
      outlined
    ></p-button>
  </div>

  <!-- State Container -->
  <app-state-container
    [state]="viewState()"
    loadingMessage="Loading applications..."
    loadingSubMessage="Please wait while we fetch your applications"
    emptyIcon="pi-th-large"
    emptyTitle="No applications found"
    [emptyMessage]="emptyStateMessage()"
    [showEmptyAction]="true"
    emptyActionLabel="Add Application"
    emptyActionIcon="pi-plus"
    [emptyActionOutlined]="true"
    [showClearFilters]="false"
    [errorMessage]="errorMessage()"
    (emptyAction)="openAddApplicationDialog()"
    (retry)="retryLoad()"
  >
    <div class="bg-white dark:bg-zinc-950 rounded-lg border border-surface-200 dark:border-zinc-800">
      <p-table
        [value]="applications()"
        [loading]="loading()"
        class="p-datatable-sm"
        [rowHover]="true"
        [resizableColumns]="true"
        columnResizeMode="fit"
        stateStorage="local"
        stateKey="app-application-table"
      >
        <ng-template pTemplate="header">
          <tr class="bg-surface-100 text-surface-600">
            <th
              pResizableColumn
              style="min-width:200px;"
              class="px-4 py-2 bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Name
            </th>
            <th
              pResizableColumn
              style="min-width:200px;"
              class="px-4 py-2 bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Client ID
            </th>
            <th
              pResizableColumn
              style="min-width:200px;"
              class="px-4 py-2 bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Code
            </th>
            <th
              pResizableColumn
              style="min-width:200px;"
              class="px-4 py-2 bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              RedirectUri
            </th>
            <th
              pResizableColumn
              style="min-width:200px;"
              class="px-4 py-2 bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              IconUrl
            </th>
            <th
              pResizableColumn
              style="min-width:200px;"
              class="px-4 py-2 bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Status
            </th>
            <th style="width: 4rem">Action</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-application>
          <tr class="border-t border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800 transition cursor-pointer" (click)="viewApplicationDetails(application)">
            <td class="px-4 py-2 text-primary-600 dark:text-primary-400 cursor-pointer underline">
              {{ application.name }}
            </td>
            <td class="px-4 py-2 text-surface-700 dark:text-surface-300">
              {{ application.id }}
            </td>
            <td class="px-4 py-2 text-surface-700 dark:text-surface-300">
              {{ application.code }}
            </td>
            <td class="px-4 py-2 text-surface-700 dark:text-surface-300">
              {{ application.defaultRedirectUri }}
            </td>
            <td class="px-4 py-2 text-surface-700 dark:text-surface-300">
              {{ application.iconUrl }}
            </td>
            <td class="px-4 py-2 text-surface-700 dark:text-surface-300">
              <span
                class="px-2 py-0.5 rounded text-xs"
                [ngClass]="{
                  'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300': application.active,
                  'bg-gray-200 text-gray-600 dark:bg-surface-700 dark:text-surface-300': !application.active
                }"
              >
                {{ application.active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td>
              <div class="flex gap-2" (click)="$event.stopPropagation()">
                <button pButton icon="pi pi-pencil" class="p-button-rounded p-button-text" (click)="editApplication(application)"></button>
                <button pButton icon="pi pi-trash" severity="danger" class="p-button-rounded p-button-text" (click)="deleteApplication(application)"></button>
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="3" class="text-center py-6 text-surface-500">
              No applications found
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </app-state-container>

  <p-dialog
    [(visible)]="showAddApplicationDialog"
    [modal]="true"
    [style]="{ width: '500px' }"
    [header]="viewMode ? 'Application Details' : (isEditMode ? 'Edit Application' : 'Add Application')"
    [draggable]="false"
    [resizable]="false"
  >
    <form [formGroup]="addApplicationForm" class="flex flex-col gap-4 mt-2">
      <!-- Name -->
      <div>
        <label class="block text-sm font-medium mb-1">
          Application Name @if (!viewMode) { <span class="text-red-500">*</span> }
        </label>
        <input
          pInputText
          formControlName="name"
          class="w-full"
          placeholder="Enter application name"
        />
        @if (!viewMode && addApplicationForm.get('name')?.touched && addApplicationForm.get('name')?.invalid) {
          <small class="text-red-500">Name is required</small>
        }
      </div>

      @if (viewMode) {
        <div>
          <label class="block text-sm font-medium mb-1">Client ID</label>
          <input
            pInputText
            formControlName="clientId"
            class="w-full"
            readonly
          />
        </div>
      }

      <!-- Code -->
      <div>
        <label class="block text-sm font-medium mb-1">
          Code @if (!viewMode) { <span class="text-red-500">*</span> }
        </label>
        <input
          pInputText
          formControlName="code"
          class="w-full"
          placeholder="Enter code"
        />
        @if (!viewMode && addApplicationForm.get('code')?.touched && addApplicationForm.get('code')?.invalid) {
          <small class="text-red-500">Code is required</small>
        }
      </div>

      <!-- Redirect URI -->
      <div>
        <label class="block text-sm font-medium mb-1">Redirect URI</label>
        <input
          pInputText
          formControlName="defaultRedirectUri"
          class="w-full"
          placeholder="enter redirect uri"
        />
      </div>

      <!-- Icon URL -->
      <div>
        <label class="block text-sm font-medium mb-1">Icon URL</label>
        <input
          pInputText
          formControlName="iconUrl"
          class="w-full"
          placeholder="enter icon url"
        />
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2 mt-4">
        <p-button
          type="button"
          [label]="viewMode ? 'Close' : 'Cancel'"
          severity="secondary"
          (onClick)="closeDialog()">
        </p-button>
        @if (!viewMode) {
          <p-button
            type="button"
            [label]="isEditMode ? 'Update' : 'Create'"
            severity="primary"
            (onClick)="createOrUpdateApplication()"
            [disabled]="addApplicationForm.invalid || (!addApplicationForm.dirty && isEditMode)">
          </p-button>
        }
      </div>
    </form>
  </p-dialog>
</app-page-layout>